/**
 * Investment Action Rules
 * Business logic for determining available actions based on investment state
 *
 * Domain Layer - Pure business logic
 */

import type { InvestmentDocumentResponse } from "@/domain/documentBlocks";
import { ReviewStatus, SignatureStatus } from "@/domain/documentEnums";

import { InvestmentState, InvestmentDocumentType } from "./types";
import type { InvestmentWithDetailsResponse } from "./types";

/** Signature statuses that mean the contract is "active" (not voided/declined/failed). */
const ACTIVE_SIGNATURE_STATUSES: SignatureStatus[] = [
  SignatureStatus.PENDING,
  SignatureStatus.SENT,
  SignatureStatus.SIGNED,
];

/** Check if there's an active contract of a specific type (signature not voided/declined). */
export const hasActiveContractOfType = (
  documents: InvestmentDocumentResponse[],
  contractType: InvestmentDocumentType,
): boolean => {
  return documents.some(
    (doc) =>
      doc.document.doc_type === contractType &&
      doc.signature != null &&
      (ACTIVE_SIGNATURE_STATUSES as string[]).includes(doc.signature.status),
  );
};

export const hasActiveReservationContract = (documents: InvestmentDocumentResponse[]): boolean =>
  hasActiveContractOfType(documents, InvestmentDocumentType.RESERVATION_CONTRACT);

export const hasActiveFinancingContract = (documents: InvestmentDocumentResponse[]): boolean =>
  hasActiveContractOfType(documents, InvestmentDocumentType.FINANCING_CONTRACT);

export const hasActiveCommunicationContract = (documents: InvestmentDocumentResponse[]): boolean =>
  hasActiveContractOfType(documents, InvestmentDocumentType.COMMUNICATION_CONTRACT);

export const hasSignedFinancingContract = (documents: InvestmentDocumentResponse[]): boolean =>
  documents.some(
    (doc) =>
      doc.document.doc_type === InvestmentDocumentType.FINANCING_CONTRACT &&
      doc.signature?.status === SignatureStatus.SIGNED,
  );

export const hasSignedCommunicationContract = (documents: InvestmentDocumentResponse[]): boolean =>
  documents.some(
    (doc) =>
      doc.document.doc_type === InvestmentDocumentType.COMMUNICATION_CONTRACT &&
      (doc.signature?.status === SignatureStatus.SIGNED ||
        doc.review.status === ReviewStatus.APPROVED),
  );

export const hasActiveTaxCertificate = (documents: InvestmentDocumentResponse[]): boolean =>
  documents.some(
    (doc) =>
      doc.document.doc_type === InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE &&
      doc.review.status !== ReviewStatus.REJECTED,
  );

export const hasApprovedTaxCertificate = (documents: InvestmentDocumentResponse[]): boolean =>
  documents.some(
    (doc) =>
      doc.document.doc_type === InvestmentDocumentType.TAX_SUBMISSION_CERTIFICATE &&
      doc.review.status === ReviewStatus.APPROVED,
  );

/**
 * Shape of a single action rule entry in ACTION_RULES.
 */
interface ActionRule {
  allowedStates: InvestmentState[];
  checkDocuments?: (documents: InvestmentDocumentResponse[]) => boolean;
  checkAdditional?: (investment: InvestmentWithDetailsResponse) => boolean;
}

/**
 * Action availability rules based on investment state and document status
 *
 * Each action has:
 * - allowedStates: Array of investment states where action is available
 * - checkDocuments: Function to validate document requirements
 */
const ACTION_RULES: Record<string, ActionRule> = {
  /**
   * Send Reservation Contract
   * Available when: Investment is in PROSPECT or RESERVED state AND no active reservation contract exists
   * Blocked when: There's already an active reservation contract (PENDING, SENT, SIGNED, or APPROVED)
   */
  sendReservationContract: {
    allowedStates: [InvestmentState.PROSPECT, InvestmentState.RESERVED],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      // Block if there's already an active reservation contract
      return !hasActiveReservationContract(documents);
    },
  },

  /**
   * Send Financing Contract
   * Available when: Investment is in PROSPECT, RESERVED, or FINANCING state AND no active financing contract exists
   * Blocked when: There's already an active financing contract (PENDING, SENT, SIGNED, or APPROVED)
   */
  sendFinancingContract: {
    allowedStates: [InvestmentState.PROSPECT, InvestmentState.RESERVED, InvestmentState.FINANCING],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      // Block if there's already an active financing contract
      return !hasActiveFinancingContract(documents);
    },
  },

  /**
   * Send Proposal
   * Available when: Investment is in PROSPECT state
   * No additional checks needed
   */
  sendProposal: {
    allowedStates: [InvestmentState.PROSPECT],
    checkDocuments: () => true,
  },

  /**
   * Edit Investment
   * Available in PROSPECT (full edit) and RESERVED/FINANCING/FUNDED (correction mode).
   * The form adapts field availability based on the investment's current state.
   * Backend handles quota rebalancing for non-PROSPECT states.
   */
  editInvestment: {
    allowedStates: [
      InvestmentState.PROSPECT,
      InvestmentState.RESERVED,
      InvestmentState.FINANCING,
      InvestmentState.FUNDED,
    ],
    checkDocuments: () => true,
  },

  /**
   * Extend Reservation
   * Available when: Investment is in RESERVED state
   * Additional check: Must have reservation_expires_at date
   */
  extendReservation: {
    allowedStates: [InvestmentState.RESERVED],
    checkAdditional: (investment: InvestmentWithDetailsResponse) => {
      return Boolean(investment.investment.reservation_expires_at);
    },
  },

  /**
   * Void Contract
   * Available when: Investment has a contract document in SENT or PENDING status
   */
  voidContract: {
    allowedStates: [
      InvestmentState.PROSPECT,
      InvestmentState.RESERVED,
      InvestmentState.FINANCING,
      InvestmentState.FUNDED,
      InvestmentState.DECLARED,
    ],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      return documents.some(
        (d) =>
          (d.document.doc_type === InvestmentDocumentType.RESERVATION_CONTRACT ||
            d.document.doc_type === InvestmentDocumentType.FINANCING_CONTRACT) &&
          (d.signature?.status === SignatureStatus.SENT ||
            d.signature?.status === SignatureStatus.PENDING),
      );
    },
  },

  /**
   * Resend Document
   * Available when: Investment has a contract document in SENT or PENDING status
   */
  resendDocument: {
    allowedStates: [
      InvestmentState.PROSPECT,
      InvestmentState.RESERVED,
      InvestmentState.FINANCING,
      InvestmentState.FUNDED,
      InvestmentState.DECLARED,
    ],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      return documents.some(
        (d) =>
          d.signature?.status === SignatureStatus.SENT ||
          d.signature?.status === SignatureStatus.PENDING,
      );
    },
  },

  /**
   * Reject Investment
   * Available when: Investment is in any active state (not already terminal)
   * Terminal states: CANCELLED, EXPIRED, REJECTED
   */
  rejectInvestment: {
    allowedStates: [
      InvestmentState.PROSPECT,
      InvestmentState.RESERVED,
      InvestmentState.FINANCING,
      InvestmentState.FUNDED,
      InvestmentState.DECLARED,
    ],
    checkDocuments: () => true, // No document requirements
  },

  /**
   * Delete Investment
   * Available in all states
   */
  deleteInvestment: {
    allowedStates: [
      InvestmentState.PROSPECT,
      InvestmentState.RESERVED,
      InvestmentState.FINANCING,
      InvestmentState.FUNDED,
      InvestmentState.DECLARED,
      InvestmentState.CANCELLED,
      InvestmentState.EXPIRED,
      InvestmentState.REJECTED,
    ],
    checkDocuments: () => true, // No document requirements
  },

  /**
   * Send Communication Contract (Comunicación AEAT)
   * Available when: Investment is in RESERVED, FINANCING or FUNDED state AND no active communication contract
   * Blocked when: Already has active communication contract
   */
  sendCommunicationContract: {
    allowedStates: [InvestmentState.RESERVED, InvestmentState.FINANCING, InvestmentState.FUNDED],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      // Only check that no active communication contract exists
      return !hasActiveCommunicationContract(documents);
    },
  },

  /**
   * Download AEAT Documents
   * Available when: Investment is in FUNDED or DECLARED state AND has signed communication contract
   * Downloads ZIP with communication contract, financing contract, and INAEM certificate
   */
  downloadAeat: {
    allowedStates: [InvestmentState.FUNDED, InvestmentState.DECLARED],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      // Requires signed communication contract to enable download
      return hasSignedCommunicationContract(documents);
    },
  },

  /**
   * Upload Tax Certificate (Justificante de Comunicación)
   * Available when: Investment is in DECLARED state AND no active tax certificate exists
   * Allows uploading the AEAT communication receipt
   */
  uploadTaxCertificate: {
    allowedStates: [InvestmentState.DECLARED],
    checkDocuments: (documents: InvestmentDocumentResponse[]) => {
      // Block if there's already an active tax certificate
      return !hasActiveTaxCertificate(documents);
    },
  },
};

/**
 * Interface for available actions result
 */
export interface AvailableInvestmentActions {
  canSendReservationContract: boolean;
  canSendFinancingContract: boolean;
  canSendCommunicationContract: boolean;
  canSendProposal: boolean;
  canEditInvestment: boolean;
  canExtendReservation: boolean;
  canVoidContract: boolean;
  canResendDocument: boolean;
  canRejectInvestment: boolean;
  canDeleteInvestment: boolean;
  /** Whether the Download AEAT action is enabled (state + documents check) */
  canDownloadAeat: boolean;
  /** Whether to show the Download AEAT action (state check only) */
  showDownloadAeat: boolean;
  /** Whether the Upload Tax Certificate action is enabled (state + documents check) */
  canUploadTaxCertificate: boolean;
  /** Whether to show the Upload Tax Certificate action (state check only) */
  showUploadTaxCertificate: boolean;
}

/**
 * Get available actions for an investment based on current state and documents
 *
 * @param investment - Investment with full details including documents
 * @returns Object with boolean flags for each available action
 */
export const getAvailableInvestmentActions = (
  investment: InvestmentWithDetailsResponse,
): AvailableInvestmentActions => {
  const currentState = investment.investment.state;
  const isArchived = investment.investment.archived;
  const documents = investment.documents;

  // If archived, no actions available
  if (isArchived) {
    return {
      canSendReservationContract: false,
      canSendFinancingContract: false,
      canSendCommunicationContract: false,
      canSendProposal: false,
      canEditInvestment: false,
      canExtendReservation: false,
      canVoidContract: false,
      canResendDocument: false,
      canRejectInvestment: false,
      canDeleteInvestment: false,
      canDownloadAeat: false,
      showDownloadAeat: false,
      canUploadTaxCertificate: false,
      showUploadTaxCertificate: false,
    };
  }

  return {
    canSendReservationContract:
      ACTION_RULES.sendReservationContract.allowedStates.includes(currentState) &&
      (ACTION_RULES.sendReservationContract.checkDocuments?.(documents) ?? true),

    canSendFinancingContract:
      ACTION_RULES.sendFinancingContract.allowedStates.includes(currentState) &&
      (ACTION_RULES.sendFinancingContract.checkDocuments?.(documents) ?? true),

    canSendCommunicationContract:
      ACTION_RULES.sendCommunicationContract.allowedStates.includes(currentState) &&
      (ACTION_RULES.sendCommunicationContract.checkDocuments?.(documents) ?? true),

    canSendProposal:
      ACTION_RULES.sendProposal.allowedStates.includes(currentState) &&
      (ACTION_RULES.sendProposal.checkDocuments?.(documents) ?? true),

    canEditInvestment:
      ACTION_RULES.editInvestment.allowedStates.includes(currentState) &&
      (ACTION_RULES.editInvestment.checkDocuments?.(documents) ?? true),

    canExtendReservation:
      ACTION_RULES.extendReservation.allowedStates.includes(currentState) &&
      (ACTION_RULES.extendReservation.checkAdditional?.(investment) ?? true),

    canVoidContract:
      ACTION_RULES.voidContract.allowedStates.includes(currentState) &&
      (ACTION_RULES.voidContract.checkDocuments?.(documents) ?? true),

    canResendDocument:
      ACTION_RULES.resendDocument.allowedStates.includes(currentState) &&
      (ACTION_RULES.resendDocument.checkDocuments?.(documents) ?? true),

    canRejectInvestment:
      ACTION_RULES.rejectInvestment.allowedStates.includes(currentState) &&
      (ACTION_RULES.rejectInvestment.checkDocuments?.(documents) ?? true),

    canDeleteInvestment:
      ACTION_RULES.deleteInvestment.allowedStates.includes(currentState) &&
      (ACTION_RULES.deleteInvestment.checkDocuments?.(documents) ?? true),

    canDownloadAeat:
      ACTION_RULES.downloadAeat.allowedStates.includes(currentState) &&
      (ACTION_RULES.downloadAeat.checkDocuments?.(documents) ?? true),

    // Show Download AEAT when in correct state (but may be disabled if no signed contract)
    showDownloadAeat: ACTION_RULES.downloadAeat.allowedStates.includes(currentState),

    canUploadTaxCertificate:
      ACTION_RULES.uploadTaxCertificate.allowedStates.includes(currentState) &&
      (ACTION_RULES.uploadTaxCertificate.checkDocuments?.(documents) ?? true),

    // Show Upload Tax Certificate when in DECLARED state (but may be disabled if already uploaded)
    showUploadTaxCertificate:
      ACTION_RULES.uploadTaxCertificate.allowedStates.includes(currentState),
  };
};

/**
 * Get human-readable reason why an action is not available
 * Useful for displaying tooltips or error messages
 *
 * @param action - The action key to check
 * @param investment - Investment with full details
 * @returns String explanation or null if action is available
 */
export const getActionUnavailableReason = (
  action: keyof AvailableInvestmentActions,
  investment: InvestmentWithDetailsResponse,
): string | null => {
  // Check if archived
  if (investment.investment.archived) {
    return "Investment is archived. Unarchive to perform actions.";
  }

  const currentState = investment.investment.state;
  const documents = investment.documents;

  switch (action) {
    case "canSendReservationContract": {
      if (!ACTION_RULES.sendReservationContract.allowedStates.includes(currentState)) {
        return `Can only send reservation contract when investment is in PROSPECT or RESERVED state. Current state: ${currentState}`;
      }
      if (hasActiveReservationContract(documents)) {
        return "Cannot send new reservation contract: an active reservation contract already exists. Please void the current contract first.";
      }
      break;
    }

    case "canSendFinancingContract": {
      if (!ACTION_RULES.sendFinancingContract.allowedStates.includes(currentState)) {
        return `Can only send financing contract when investment is in PROSPECT, RESERVED or FINANCING state. Current state: ${currentState}`;
      }
      if (hasActiveFinancingContract(documents)) {
        return "Cannot send new financing contract: an active financing contract already exists. Please void the current contract first.";
      }
      break;
    }

    case "canSendCommunicationContract": {
      if (!ACTION_RULES.sendCommunicationContract.allowedStates.includes(currentState)) {
        return `Can only send communication contract when investment is in RESERVED, FINANCING or FUNDED state. Current state: ${currentState}`;
      }
      if (hasActiveCommunicationContract(documents)) {
        return "Cannot send new communication contract: an active communication contract already exists. Please void the current contract first.";
      }
      break;
    }

    case "canSendProposal": {
      if (!ACTION_RULES.sendProposal.allowedStates.includes(currentState)) {
        return `Can only send proposal when investment is in PROSPECT state. Current state: ${currentState}`;
      }
      break;
    }

    case "canEditInvestment": {
      if (!ACTION_RULES.editInvestment.allowedStates.includes(currentState)) {
        return `Can only edit investment when in PROSPECT state. Current state: ${currentState}`;
      }
      break;
    }

    case "canExtendReservation": {
      if (!ACTION_RULES.extendReservation.allowedStates.includes(currentState)) {
        return `Can only extend reservation when investment is in RESERVED state. Current state: ${currentState}`;
      }
      if (!investment.investment.reservation_expires_at) {
        return "Investment does not have a reservation expiration date set.";
      }
      break;
    }

    case "canVoidContract": {
      if (!ACTION_RULES.voidContract.allowedStates.includes(currentState)) {
        return `Cannot void contracts in ${currentState} state.`;
      }
      if (!(ACTION_RULES.voidContract.checkDocuments?.(documents) ?? true)) {
        return "No active contract documents available to void.";
      }
      break;
    }

    case "canResendDocument": {
      if (!ACTION_RULES.resendDocument.allowedStates.includes(currentState)) {
        return `Cannot resend documents in ${currentState} state.`;
      }
      if (!(ACTION_RULES.resendDocument.checkDocuments?.(documents) ?? true)) {
        return "No pending documents available to resend.";
      }
      break;
    }

    case "canRejectInvestment": {
      if (!ACTION_RULES.rejectInvestment.allowedStates.includes(currentState)) {
        return `Cannot reject investment in ${currentState} state.`;
      }
      break;
    }

    case "canDeleteInvestment": {
      if (!ACTION_RULES.deleteInvestment.allowedStates.includes(currentState)) {
        return `Cannot delete investment in terminal state: ${currentState}.`;
      }
      break;
    }

    case "canDownloadAeat": {
      if (!ACTION_RULES.downloadAeat.allowedStates.includes(currentState)) {
        return `Can only download AEAT documents when investment is in FUNDED or DECLARED state. Current state: ${currentState}`;
      }
      if (!hasSignedCommunicationContract(documents)) {
        return "Cannot download AEAT documents: communication contract must be signed or approved first.";
      }
      break;
    }

    case "canUploadTaxCertificate": {
      if (!ACTION_RULES.uploadTaxCertificate.allowedStates.includes(currentState)) {
        return `Can only upload tax certificate when investment is in DECLARED state. Current state: ${currentState}`;
      }
      if (hasActiveTaxCertificate(documents)) {
        return "Cannot upload new tax certificate: a tax certificate already exists.";
      }
      break;
    }

    default:
      return null;
  }

  return null;
};
